import { Alert, Badge } from '@dreamtree-org/twreact-ui';
import { PageHeader, SectionCard, QueryState, StatCard, EmptyState } from '@/components';
import { useQuery } from '@/lib/useQuery';
import DataTable from '../components/DataTable';
import BarList from '../components/BarList';
import Heatmap from '../components/Heatmap';
import { BarChart, LineChart } from '@dreamtree-org/twreact-ui/charts';
import { funnel } from '../lib/metrics';
import { formatDate } from '@/lib/format';

const breachVariant = (pct) => (pct >= 15 ? 'error' : pct >= 8 ? 'warning' : 'success');
const hours = (h) => (h == null ? '—' : `${h} h`);

/**
 * ADMIN-06 Risk & operations across every store, last six weeks: the purchase funnel
 * and channels from the first-party storefront beacon (D22) plus paid / delivered
 * orders, SLA performance, device defects, refund rates, return reasons and KPIs.
 */
export default function Risk() {
  const query = useQuery('platformRisk', undefined, { select: (r) => r.data });
  return (
    <div className="space-y-6">
      <PageHeader title="Risk & operations" subtitle="ADMIN-06 · conversion funnel, SLA performance, device defects and refunds" />
      <QueryState query={query}>
        {(d) => {
          const steps = funnel(d.funnel);
          return (
            <>
              <p className="text-sm opacity-70">{d.period} · all merchants · as of {formatDate(d.as_of)}</p>
              {d.unreachable.length ? <Alert variant="warning" title="Some stores could not be read">{d.unreachable.join(', ')} are missing from these numbers.</Alert> : null}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <StatCard label="Commission margin" value={`${d.kpis.commission_margin_pct}%`} hint="of delivered GMV" />
                <StatCard label="On-time dispatch" value={`${d.kpis.on_time_dispatch_pct}%`} hint="within 24 h of acceptance" />
                <StatCard label="Inspection fails" value={`${d.kpis.inspection_fail_pct}%`} hint="of inspection reports" />
                <StatCard label="Return rate" value={`${d.kpis.return_rate_pct}%`} hint="returns per delivered order" />
                <StatCard label="Dispute rate" value={`${d.kpis.dispute_rate_pct}%`} hint="disputes per delivered order" />
              </div>

              <div className="grid gap-4 2xl:grid-cols-2">
                <SectionCard title="Purchase funnel">
                  <p className="text-xs opacity-60">
                    Sessions to order placed: storefront beacon{d.tracking_since ? ` (since ${formatDate(d.tracking_since)})` : ' (no events yet)'}. Paid and delivered: orders.
                  </p>
                  <BarList
                    ariaLabel="Purchase funnel"
                    rows={steps.map((s) => ({ label: s.step, value: s.count, display: s.count.toLocaleString('en-IN'), hint: s.source === 'orders' ? 'from orders' : `${s.pctOfTop}% · ${s.pctOfPrev}% of prev` }))}
                  />
                </SectionCard>
                <SectionCard title="Channel acquisition">
                  {d.channels.length ? (
                    <DataTable
                      data={d.channels.map((c) => ({ ...c, conversion: c.sessions ? Math.round((c.orders / c.sessions) * 10000) / 100 : 0 }))}
                      columns={[
                        { key: 'channel', label: 'Channel', render: (c) => <span className="font-medium">{c.channel}</span> },
                        { key: 'sessions', label: 'Sessions', render: (c) => c.sessions.toLocaleString('en-IN') },
                        { key: 'orders', label: 'Orders' },
                        { key: 'conversion', label: 'Conversion', render: (c) => `${c.conversion}%` },
                      ]}
                    />
                  ) : <EmptyState title="No storefront sessions yet">Channels appear once shoppers visit a store.</EmptyState>}
                </SectionCard>
              </div>

              <SectionCard title="SLA performance">
                <DataTable
                  data={d.sla}
                  columns={[
                    { key: 'stage', label: 'Stage', render: (s) => <span className="font-medium">{s.stage}</span> },
                    { key: 'target_h', label: 'Target', render: (s) => `${s.target_h} h` },
                    { key: 'p50_h', label: 'p50', hideOnMobile: true, render: (s) => hours(s.p50_h) },
                    { key: 'p90_h', label: 'p90', render: (s) => <span className={s.p90_h != null && s.p90_h > s.target_h ? 'text-error-600 font-medium' : ''}>{hours(s.p90_h)}</span> },
                    { key: 'breach_pct', label: 'Breached', render: (s) => (s.volume ? <Badge variant={breachVariant(s.breach_pct)} size="sm">{s.breach_pct}%</Badge> : '—') },
                    { key: 'volume', label: 'Volume', hideOnMobile: true },
                  ]}
                />
              </SectionCard>

              <div className="grid gap-4 2xl:grid-cols-2">
                <SectionCard title="Device defects by brand (returns + warranty claims)">
                  {d.defects.length ? <Heatmap rows={d.defects} rowKey="brand" rowLabel="Brand" columns={d.defect_columns} legend="Fewer" /> : <EmptyState title="No returns or warranty claims in this period" />}
                </SectionCard>
                <SectionCard title="Refund rate by merchant and week (%)">
                  <Heatmap rows={d.refunds} rowKey="merchant" rowLabel="Merchant" columns={d.refund_columns} format={(v) => `${v}%`} legend="Lower" />
                </SectionCard>
              </div>

              {/* MERCH-89: returns by product type and condition, from each returned line's snapshot. */}
              <SectionCard title="Returns by product type and condition">
                {(d.returns_by_kind || []).length ? (
                  <DataTable
                    data={d.returns_by_kind}
                    columns={[
                      { key: 'product_type_label', label: 'Product type', render: (r) => <span className="font-medium">{r.product_type_label}</span> },
                      { key: 'industry_label', label: 'Industry', hideOnMobile: true, render: (r) => r.industry_label || '—' },
                      { key: 'condition_label', label: 'Condition', render: (r) => <Badge variant="outline" size="sm">{r.condition_label}</Badge> },
                      { key: 'count', label: 'Returned lines' },
                    ]}
                  />
                ) : <EmptyState title="No returns in this period" />}
              </SectionCard>

              <div className="grid gap-4 lg:grid-cols-2">
                <SectionCard title="Return reasons">
                  {d.return_reasons.length
                    ? <BarChart title="Return reasons" data={d.return_reasons} index="reason" categories={['count']} horizontal showLegend={false} yAxisWidth={170} height={260} />
                    : <EmptyState title="No returns in this period" />}
                </SectionCard>
                <SectionCard title="Weekly orders, refunds and disputes">
                  <LineChart title="Weekly orders, refunds and disputes" data={d.weekly} index="week" categories={['orders', 'refunds', 'disputes']} colors={['primary', 'warning', 'error']} height={200} />
                  <DataTable
                    data={d.weekly}
                    sortable={false}
                    columns={[
                      { key: 'week', label: 'Week' },
                      { key: 'orders', label: 'Orders' },
                      { key: 'refunds', label: 'Refunds' },
                      { key: 'disputes', label: 'Disputes' },
                      { key: 'rate', label: 'Refund rate', render: (w) => (w.orders ? `${Math.round((w.refunds / w.orders) * 1000) / 10}%` : '—') },
                    ]}
                  />
                </SectionCard>
              </div>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}
